import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { formatDistanceToNow } from 'date-fns';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc, orderBy, addDoc, updateDoc, limit } from 'firebase/firestore';
import { FaSearch, FaMapMarkerAlt, FaLock, FaFileContract, FaCheckCircle, FaExclamationTriangle, FaUserShield, FaArrowRight } from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Spinner from '../components/Spinner';

const ProfessionalDashboard = ({ adminView = false, userId: propUserId = null }) => {
  // Get user ID from props or URL
  const searchParams = new URLSearchParams(window.location.search);
  const urlAdminView = searchParams.get('adminView') === 'true';
  const urlTargetUserId = searchParams.get('userId');
  
  // Use prop values if available, otherwise fall back to URL params
  const isAdminView = adminView || urlAdminView;
  const targetUserId = propUserId || urlTargetUserId;
  
  // State management
  const [profile, setProfile] = useState(null);
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    location: '',
    jobType: '',
    experience: '',
    locations: [] // Store available locations for the dropdown
  });
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: '',
    message: '',
    showUpgradeButton: false,
    onConfirm: null
  });
  
  const navigate = useNavigate();

  // Check if admin view is enabled and fetch target user's data
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (isAdminView) {
        try {
          // In admin view, we trust the parent component has already verified admin status
          setIsAdminMode(true);
          
          // If we have a target user ID, fetch that user's profile
          if (targetUserId) {
            // Try to get from professionals collection first
            let targetUserDoc = await getDoc(doc(db, 'professionals', targetUserId));
            
            // If not found in professionals, try users collection
            if (!targetUserDoc.exists()) {
              targetUserDoc = await getDoc(doc(db, 'users', targetUserId));
            }
            
            if (targetUserDoc.exists()) {
              const userData = targetUserDoc.data();
              setViewingUser({
                id: targetUserId,
                ...userData
              });
              setProfile({
                id: targetUserId,
                ...userData
              });
              setLoading(false);
            } else {
              setError('User not found');
              setLoading(false);
            }
          } else {
            // If no target user ID, we're in admin view but no specific user to view
            setError('No user specified');
            setLoading(false);
          }
        } catch (err) {
          console.error('Error in admin view:', err);
          setError('Error loading admin view');
          setLoading(false);
        }
      }
    };
    
    if (isAdminView) {
      checkAdminStatus();
    } else {
      // Not in admin view, proceed with normal flow
      setLoading(false);
    }
  }, [isAdminView, targetUserId, navigate]);

  // Fetch user's profile and jobs (only if not in admin mode)
  useEffect(() => {
    if (!adminView) {
      fetchProfileAndJobs();
    }
  }, [adminView]);

  // Helper function to fetch professional by userId
  const fetchProfessionalByUserId = async (userId) => {
    try {
      // First try to get the user document to check for professionalId
      const userDoc = await getDoc(doc(db, 'users', userId));
      
      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }
      
      const userData = userDoc.data();
      
      // If user has a professionalId, try to fetch using that first
      if (userData.professionalId) {
        try {
          const professionalDoc = await getDoc(doc(db, 'professionals', userData.professionalId));
          if (professionalDoc.exists()) {
            const profileData = {
              id: professionalDoc.id,
              ...professionalDoc.data()
            };
            setProfile(profileData);
            return profileData;
          }
        } catch (err) {
          console.warn('Error fetching professional by ID, falling back to user ID query:', err);
        }
      }
      
      // Fall back to querying by userId
      const profileQuery = query(
        collection(db, 'professionals'),
        where('userId', '==', userId),
        limit(1)
      );
      
      const profileSnapshot = await getDocs(profileQuery);
      
      if (!profileSnapshot.empty) {
        const docData = profileSnapshot.docs[0];
        const profileData = {
          id: docData.id,
          ...docData.data()
        };
        
        // Update user document with professionalId for future lookups
        try {
          await updateDoc(doc(db, 'users', userId), {
            professionalId: docData.id
          });
        } catch (updateErr) {
          console.warn('Could not update user with professionalId:', updateErr);
        }
        
        setProfile(profileData);
        return profileData;
      }
      
      // If we get here, no profile exists - create one
      return await createProfessionalProfile(userId, userData);
      
    } catch (err) {
      console.error('Error in fetchProfessionalByUserId:', err);
      throw err;
    }
  };
  
  // Create a new professional profile
  const createProfessionalProfile = async (userId, userData = null) => {
    try {
      // If userData not provided, fetch it
      if (!userData) {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (!userDoc.exists()) {
          throw new Error('User document not found');
        }
        userData = userDoc.data();
      }
      
      const professionalData = {
        userId: userId,
        email: userData.email || '',
        displayName: userData.displayName || '',
        photoURL: userData.photoURL || '',
        role: userData.role || 'therapist', // Default to 'therapist' if role not set
        status: 'active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        specialties: [],
        experience: 0,
        bio: '',
        location: {},
        isProfileComplete: false,
        // Add other default fields that might be required
        qualifications: [],
        availability: {},
        education: [],
        certifications: [],
        languages: []
      };
      
      // Create the professional profile
      const professionalRef = await addDoc(collection(db, 'professionals'), professionalData);
      
      // Update user document with the new professionalId
      try {
        const userDocRef = doc(db, 'users', userId);
        await updateDoc(userDocRef, {
          professionalId: professionalRef.id
        });
      } catch (updateErr) {
        console.warn('Could not update user with professionalId:', updateErr);
        // Continue even if we can't update the user document
      }
      
      const newProfile = {
        id: professionalRef.id,
        ...professionalData
      };
      
      setProfile(newProfile);
      console.log('Created new professional profile for user:', userId);
      return newProfile;
      
    } catch (err) {
      console.error('Error in createProfessionalProfile:', err);
      // Instead of throwing, return a minimal profile to prevent UI errors
      const minimalProfile = {
        id: 'local-profile',
        userId: userId,
        email: userData?.email || '',
        displayName: userData?.displayName || 'New User',
        isProfileComplete: false,
        status: 'active'
      };
      setProfile(minimalProfile);
      return minimalProfile;
    }
  };

  const fetchProfileAndJobs = async () => {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        navigate('/login?redirect=' + encodeURIComponent(window.location.pathname));
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        // First try to fetch the professional profile
        await fetchProfessionalByUserId(currentUser.uid);
        
        // If we get here, profile was fetched or created successfully
        // Now fetch jobs if needed
        await fetchJobs(currentUser.uid);
        
      } catch (profileErr) {
        console.error('Error in profile handling:', profileErr);
        setError('Error loading profile data. Some features may be limited.');
      }
      
    } catch (err) {
      console.error('Unexpected error in fetchProfileAndJobs:', err);
      setError('An unexpected error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Process job document data
  const processJobDoc = (doc) => {
    const data = doc.data();
    
    // Safely handle dates
    const getValidDate = (dateValue) => {
      try {
        if (dateValue?.toDate) {
          const d = dateValue.toDate();
          return isNaN(d.getTime()) ? new Date() : d;
        }
        if (dateValue instanceof Date) {
          return isNaN(dateValue.getTime()) ? new Date() : dateValue;
        }
        return new Date();
      } catch (e) {
        return new Date();
      }
    };
    
    const postedAt = getValidDate(data.postedAt || data.createdAt);
    
    return {
      id: doc.id,
      ...data,
      postedAt, // Use the safely processed date
      // Ensure we have a valid company name
      company: data.company || data.organization?.name || 'Anonymous',
      // Ensure we have a valid poster role
      posterRole: data.posterRole || data.organization?.type || 'Organization',
      // Ensure we have a valid location object
      location: data.location || {},
      source: data.source || 'jobPosts'
    };
  };

  // Separate function to fetch jobs
  const fetchJobs = async (userId) => {
    try {
      console.log('Fetching jobs...');
      let jobsList = [];
      
      // Try fetching from jobPosts collection without status filter first
      try {
        let jobsQuery = query(
          collection(db, 'jobPosts'),
          orderBy('createdAt', 'desc'),
          limit(10)
        );
        
        let jobsSnapshot = await getDocs(jobsQuery);
        console.log(`Found ${jobsSnapshot.size} jobPosts`);
        
        // Process each job document
        jobsList = jobsSnapshot.docs.map(processJobDoc);
      } catch (jobPostsErr) {
        console.warn('Error fetching jobPosts:', jobPostsErr);
      }
      
      // If no jobPosts found or error, try cdcJobs
      if (jobsList.length === 0) {
        console.log('No jobPosts found or error, trying cdcJobs...');
        try {
          let cdcQuery = query(
            collection(db, 'cdcJobs'),
            orderBy('createdAt', 'desc'),
            limit(10)
          );
          
          let cdcSnapshot = await getDocs(cdcQuery);
          console.log(`Found ${cdcSnapshot.size} cdcJobs`);
          
          const cdcJobs = cdcSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            source: 'cdcJobs'
          }));
          
          jobsList = [...jobsList, ...cdcJobs];
        } catch (cdcErr) {
          console.warn('Error fetching cdcJobs:', cdcErr);
        }
      }
      
      console.log(`Total jobs found: ${jobsList.length}`);
      setJobs(jobsList);
      return jobsList;
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setJobs([]); // Set empty array on error
      return [];
    }
  };
  // Function to check all job posts and their statuses (for debugging)
  const checkAllJobPosts = async () => {
    try {
      const allJobsQuery = collection(db, 'jobPosts');
      const allJobsSnapshot = await getDocs(allJobsQuery);
      
      console.log('=== JOB POSTS IN DATABASE ===');
      if (allJobsSnapshot.empty) {
        console.log('No job posts found in the database.');
      } else {
        allJobsSnapshot.docs.forEach(doc => {
          console.log(`Job ID: ${doc.id}`, doc.data());
        });
      }
      console.log('============================');
    } catch (error) {
      console.error('Error checking job posts:', error);
    }
  };

  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      navigate('/login');
      return;
    }
    
    // Check user's subscription status
  const checkSubscription = async () => {
    // Skip subscription check in admin view
    if (isAdminView) return;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Check if user is a therapist or other professional role
        const isProfessional = ['therapist', 'professional', 'shadow_teacher', 'special_educator', 'psychologist', 'doctor'].includes(
          (userData.role || '').toLowerCase()
        );
        
        // If not a professional, no need for subscription check
        if (!isProfessional) return;
        
        // Check if user has an active subscription or is within trial period
        const now = new Date();
        const trialEndDate = userData.trialEndDate?.toDate();
        const hasActiveSubscription = userData.subscriptionStatus === 'active' || 
                                    (trialEndDate && trialEndDate > now);
        
        // If no active subscription and trial has ended, show upgrade modal
        if (!hasActiveSubscription && userData.trialEndDate?.toDate() < new Date()) {
          setModalContent({
            title: 'Trial Expired',
            message: 'Your free trial has ended. Please upgrade to continue using premium features.',
            showUpgradeButton: true,
            onConfirm: () => navigate('/membership')
          });
          setShowModal(true);
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };
  
  // Call the subscription check
  checkSubscription();
    
    // Check all job posts on component mount
    checkAllJobPosts();

    const fetchData = async () => {
      try {
        // Fetch user profile
        const profileRef = doc(db, 'users', user.uid);
        const profileSnap = await getDoc(profileRef);
        
        if (profileSnap.exists()) {
          setProfile(profileSnap.data());
        }

        // First, get all jobs regardless of status for debugging
        const allJobsQuery = collection(db, 'jobPosts');
        const allJobsSnapshot = await getDocs(allJobsQuery);
        
        console.log('=== ALL JOBS IN DATABASE ===');
        allJobsSnapshot.docs.forEach(doc => {
          console.log(`Job ${doc.id}:`, {
            ...doc.data(),
            // Add status or default to 'active' if not set
            status: doc.data().status || 'active'
          });
        });
        console.log('============================');
        
        // Now filter for active jobs
        const activeJobs = allJobsSnapshot.docs.filter(doc => {
          // Consider a job active if status is 'active' or if status is not set
          const status = doc.data().status || 'active';
          return status === 'active';
        });
        
        console.log(`Found ${activeJobs.length} active jobs out of ${allJobsSnapshot.docs.length} total jobs`);
        
        // Convert to the format expected by the rest of the code
        const jobsSnapshot = {
          docs: activeJobs,
          empty: activeJobs.length === 0
        };

        console.log('=== RAW JOB DATA ===');
        jobsSnapshot.docs.forEach(doc => {
          console.log(`Job ${doc.id}:`, doc.data());
        });
        console.log('===================');
        
        // Get all jobs with their organization data
        const jobsData = await Promise.all(jobsSnapshot.docs.map(async (jobDoc) => {
          const jobData = jobDoc.data();
          let organizationData = { name: 'Anonymous' };
          
          console.log('Processing job:', jobDoc.id, jobData);
          
          // If job has a postedById, fetch the organization details
          if (jobData.postedById) {
            console.log('Fetching organization for job:', jobData.postedById);
            try {
              const orgDoc = await getDoc(doc(db, 'organizations', jobData.postedById));
              console.log('Organization data:', orgDoc.exists() ? orgDoc.data() : 'Not found');
              if (orgDoc.exists()) {
                organizationData = orgDoc.data();
              }
            } catch (error) {
              console.error('Error fetching organization data:', error);
            }
          }
          
          // Handle location - check for different possible location fields
          const location = jobData.location || jobData.jobLocation || jobData.city || 'Location not specified';
          
          // Determine poster role based on organization type or default to 'Parent'
          const getPosterRole = () => {
            if (!organizationData || organizationData.name === 'Anonymous') return 'Parent';
            if (organizationData.type) return organizationData.type;
            if (organizationData.isSchool) return 'School';
            if (organizationData.isCDC) return 'CDC';
            return 'Organization';
          };

          const jobWithOrg = {
            id: jobDoc.id,
            ...jobData,
            company: organizationData.organizationName || organizationData.name || 'Anonymous',
            location: location,
            posterRole: getPosterRole()
          };
          
          console.log('Processed job with org:', jobWithOrg);
          return jobWithOrg;
        }));
        
        // Extract unique locations for the dropdown
        console.log('Extracting locations from jobs:', jobsData);
        const locations = [...new Set(
          jobsData
            .filter(job => {
              const hasLocation = job.location && job.location !== 'Location not specified';
              console.log(`Job ${job.id} location:`, job.location, 'Valid:', hasLocation);
              return hasLocation;
            })
            .map(job => job.location)
        )].sort();
        
        console.log('Final locations array:', locations);
        
        console.log('Available locations:', locations);
        console.log('All jobs data:', jobsData);
        
        setJobs(jobsData);
        setFilteredJobs(jobsData);
        
        // Update locations in the filters state
        setFilters(prev => ({
          ...prev,
          locations // This will be used to populate the dropdown
        }));
        
        // Fetch user's applications - using jobApplications collection to match security rules
        const applicationsQuery = query(
          collection(db, 'jobApplications'),
          where('userId', '==', user.uid)
        );
        console.log('Fetching applications for user:', user.uid);
        const applicationsSnapshot = await getDocs(applicationsQuery);
        const applicationsData = applicationsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setApplications(applicationsData);
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const checkMembershipRequirement = () => {
    if (!profile) return { requiresMembership: true };
    
    const userCreatedAt = profile.createdAt ? new Date(profile.createdAt) : new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    
    const isNewUser = userCreatedAt > threeMonthsAgo;
    const hasFreeApplications = (profile.freeApplicationsUsed || 0) < 5;
    
    return {
      requiresMembership: !isNewUser && !hasFreeApplications,
      isNewUser,
      freeApplicationsLeft: 5 - (profile.freeApplicationsUsed || 0)
    };
  };

  const handleApply = async (jobId, jobTitle) => {
    const auth = getAuth();
    let user = auth.currentUser;
    
    if (!user) {
      console.log('No user found, redirecting to login');
      navigate('/login');
      return;
    }
    
    try {
      console.log('Starting application process for job:', jobId);
      
      console.log('User authenticated:', user.uid);
      const membershipCheck = checkMembershipRequirement();
      
      if (membershipCheck.requiresMembership) {
        console.log('Membership upgrade required');
        setModalContent({
          title: 'Upgrade to Pro Required',
          message: `You've used all your free applications. To apply for more jobs, please upgrade to a Pro membership.`,
          showUpgradeButton: true,
          onConfirm: () => navigate('/membership')
        });
        setShowModal(true);
        return;
      }

      console.log('Checking job details for:', jobId);
      setLoading(true);
      
      if (!user?.uid) {
        throw new Error('User not properly authenticated');
      }

      // Get the job details first to ensure it exists
      let jobDoc = await getDoc(doc(db, 'jobPosts', jobId));
      let jobData = null;
      let jobSource = 'jobPosts';
      
      // If not found in jobPosts, try cdcJobs
      if (!jobDoc.exists()) {
        jobDoc = await getDoc(doc(db, 'cdcJobs', jobId));
        jobSource = 'cdcJobs';
      }
      
      if (!jobDoc.exists()) {
        throw new Error('Job not found in any collection');
      }
      
      jobData = jobDoc.data();
      
      // Check if already applied
      const applicationsRef = collection(db, 'jobApplications');
      const q = query(
        applicationsRef,
        where('userId', '==', user.uid),
        where('jobId', '==', jobId)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        setModalContent({
          title: 'Already Applied',
          message: `You've already applied for ${jobTitle || 'this position'}.`
        });
        setShowModal(true);
        return;
      }

      // Get user data
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        // User document doesn't exist, redirect to profile setup
        navigate('/app/profile');
        return;
      }

      const userData = userDoc.data();
      
      // Check if user is a therapist or other professional role
      const isProfessional = ['therapist', 'professional', 'shadow_teacher', 'special_educator', 'psychologist', 'doctor'].includes(
        (userData.role || '').toLowerCase()
      );
      
      // Only check subscription for non-professional users
      if (!isProfessional) {
        // Check if user is one of the first 100 users (free trial)
        const isFirst100User = userData.subscriptionType === 'free_trial' && 
          userData.trialStartDate && 
          userData.trialEndDate &&
          new Date(userData.trialStartDate.toDate()) < new Date('2023-12-31') &&
          new Date() < new Date(userData.trialEndDate.toDate());
        
        // Check for paid plan
        const hasPaidPlan = userData.plan && 
          (userData.plan.startsWith('pro_') || 
           userData.plan.startsWith('org_') || 
           userData.plan.startsWith('cdc_') || 
           userData.plan.startsWith('school_'));
        
        // If user doesn't have a valid subscription or trial has expired
        if (!isFirst100User && !hasPaidPlan) {
          setModalContent({
            title: 'Upgrade Required',
            message: 'You need an active subscription to apply for jobs. Start your free trial today!',
            showUpgradeButton: true,
            onConfirm: () => navigate('/membership')
          });
          setShowModal(true);
          return;
        }
      }

      // Ensure we have a valid job title
      const safeJobTitle = jobData?.title || jobTitle || 'Untitled Position';
      
      // Create application with all required fields
      const applicationData = {
        jobId,
        jobTitle: safeJobTitle,  // Now guaranteed to be a string
        userId: user.uid,
        userEmail: user.email,
        userName: user.displayName || 'Anonymous',
        postedById: jobData?.postedById || 'unknown',
        jobData: {
          company: jobData?.company || 'Unknown Company',
          location: jobData?.location || 'Location not specified',
          type: jobData?.type || 'Full-time',
          salary: jobData?.salary || 'Not specified',
          source: jobSource  // Track which collection this job came from
        },
        status: 'applied',
        appliedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        resumeUrl: profile?.resumeUrl || ''
      };
      
      // Add the application to Firestore
      const docRef = await addDoc(collection(db, 'jobApplications'), applicationData);
      
      // Update user's free applications count if needed
      if (!membershipCheck.isNewUser) {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          freeApplicationsUsed: (profile.freeApplicationsUsed || 0) + 1
        });
      }

      // Update UI
      setApplications(prev => [...prev, {
        jobId,
        jobTitle,
        status: 'pending',
        appliedAt: new Date().toISOString()
      }]);

      // Show success message
      setModalContent({
        title: 'Application Submitted',
        message: `Your application for ${jobTitle || 'the position'} has been submitted successfully!`,
        showSuccess: true
      });
      setShowModal(true);
      
      // Refresh the user's profile to get updated application count
      const updatedProfileRef = doc(db, 'users', user.uid);
      const updatedProfileSnap = await getDoc(updatedProfileRef);
      if (updatedProfileSnap.exists()) {
        setProfile(updatedProfileSnap.data());
      }
      
      // Refresh the jobs list to show the updated application status
      const updatedJobs = [];
      
      // Get jobs from jobPosts
      try {
        const jobPostsQuery = query(collection(db, 'jobPosts'), where('status', '==', 'active'));
        const jobPostsSnapshot = await getDocs(jobPostsQuery);
        const jobPostsData = jobPostsSnapshot.docs.map(processJobDoc);
        updatedJobs.push(...jobPostsData);
      } catch (jobPostsErr) {
        console.warn('Error refreshing jobPosts:', jobPostsErr);
      }
      
      // Get jobs from cdcJobs
      try {
        const cdcJobsQuery = query(collection(db, 'cdcJobs'), where('status', '==', 'active'));
        const cdcJobsSnapshot = await getDocs(cdcJobsQuery);
        const cdcJobsData = cdcJobsSnapshot.docs.map(processJobDoc);
        updatedJobs.push(...cdcJobsData);
      } catch (cdcJobsErr) {
        console.warn('Error refreshing cdcJobs:', cdcJobsErr);
      }
      
      setJobs(updatedJobs);
      setFilteredJobs(updatedJobs);
    } catch (error) {
      // Get fresh user reference in case of error
      const currentUser = getAuth().currentUser;
      
      console.error('Error in handleApply:', {
        error,
        jobId,
        userId: currentUser?.uid,
        timestamp: new Date().toISOString()
      });
      
      let errorMessage = 'Failed to submit application. ';
      
      // Provide more specific error messages based on the error
      if (error.code === 'permission-denied') {
        errorMessage += 'You do not have permission to perform this action. ';
        errorMessage += 'Please ensure you are logged in and have the necessary permissions.';
      } else if (error.message?.includes('not-found') || error.code === 'not-found') {
        errorMessage += 'The job could not be found. It may have been removed.';
      } else if (error.message?.includes('unauthenticated') || error.code === 'unauthenticated') {
        errorMessage = 'Your session has expired. Please log in and try again.';
        // Redirect to login after a short delay
        setTimeout(() => navigate('/login'), 2000);
      } else {
        errorMessage += 'An unexpected error occurred. Please try again later.';
      }
      
      setModalContent({
        title: 'Error',
        message: errorMessage,
        showUpgradeButton: false,
        showRetryButton: !error.message?.includes('unauthenticated'), // Don't show retry if unauthenticated
        onRetry: () => handleApply(jobId, jobTitle)
      });
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) {
      setFilteredJobs(jobs);
      return;
    }

    const query = searchQuery.toLowerCase();
    const results = jobs.filter(job => 
      job.title?.toLowerCase().includes(query) ||
      job.description?.toLowerCase().includes(query) ||
      job.skills?.some(skill => skill.toLowerCase().includes(query)) ||
      job.location?.toLowerCase().includes(query)
    );

    setFilteredJobs(results);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    let results = [...jobs];
    
    if (filters.location) {
      results = results.filter(job => 
        job.location?.toLowerCase() === filters.location.toLowerCase()
      );
    }
    
    if (filters.jobType) {
      results = results.filter(job => 
        job.type?.toLowerCase() === filters.jobType.toLowerCase()
      );
    }
    
    if (filters.experience) {
      results = results.filter(job => 
        job.experienceLevel?.toLowerCase() === filters.experience.toLowerCase()
      );
    }
    
    setFilteredJobs(results);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 text-center py-10">
        {error}
      </div>
    );
  }

  // Admin banner component
  const AdminBanner = () => (
    <div className="bg-blue-900 text-white py-1 px-4 text-sm">
      <div className="container mx-auto flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <FaUserShield className="text-yellow-300" />
          <span>Admin Mode: Viewing {viewingUser?.name || 'User'}'s Dashboard</span>
        </div>
        <button 
          onClick={() => window.history.back()}
          className="text-white hover:text-yellow-200 text-sm flex items-center space-x-1"
        >
          <span>Back to Admin</span>
          <FaArrowRight className="text-xs" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {isAdminMode && <AdminBanner />}
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-100 text-red-700 border border-red-400 rounded-md">
            {error}
          </div>
        )}
        
        {/* Section 1: Opportunities (Job Cards Area) */}
        <section id="opportunities" className="mb-12">
          <div className="mb-6">
            <h2 className="text-3xl font-semibold text-violet-700 mb-6 flex items-center">
              <FaSearch className="mr-3 text-indigo-500" />
              Opportunities
            </h2>
          </div>

          {/* Filters Panel */}
          <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <select
                  name="location"
                  value={filters.location}
                  onChange={handleFilterChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">All Locations</option>
                  {filters.locations && filters.locations.map((location, index) => (
                    <option key={index} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Type</label>
                <select
                  name="jobType"
                  value={filters.jobType}
                  onChange={handleFilterChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">All Types</option>
                  <option value="full-time">Full-time</option>
                  <option value="part-time">Part-time</option>
                  <option value="contract">Contract</option>
                  <option value="internship">Internship</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Experience Level</label>
                <select
                  name="experience"
                  value={filters.experience}
                  onChange={handleFilterChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">All Levels</option>
                  <option value="entry">Entry Level</option>
                  <option value="mid">Mid Level</option>
                  <option value="senior">Senior Level</option>
                </select>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={applyFilters}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Apply Filters
              </button>
            </div>
          </div>

          {/* Job Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredJobs.length > 0 ? (
              filteredJobs.map(job => {
                const hasApplied = applications.some(app => app.jobId === job.id);
                
                return (
                  <div key={job.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-300">
                      {/* Job Header with Image */}
                      <div className="h-2 bg-gradient-to-r from-blue-500 to-purple-600"></div>
                      
                      <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-900 mb-1 line-clamp-1">{job.title}</h3>
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="text-sm font-medium text-gray-600">{job.company || 'Anonymous'}</span>
                              {job.posterRole && (
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  job.posterRole.toLowerCase() === 'school' 
                                    ? 'bg-blue-100 text-blue-800' 
                                    : job.posterRole.toLowerCase() === 'cdc' 
                                      ? 'bg-purple-100 text-purple-800' 
                                      : 'bg-green-100 text-green-800'
                                }`}>
                                  {job.posterRole}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                            {job.type || 'Full-time'}
                          </span>
                        </div>
                        
                        {/* Location and Salary */}
                        <div className="flex flex-col space-y-2 mb-4">
                          <div className="flex items-center text-sm text-gray-600">
                            <FaMapMarkerAlt className="mr-2 text-gray-400" />
                            <span>
                              {job.location ? (
                                typeof job.location === 'object' ? 
                                  `${job.location.localArea || ''}${job.location.localArea && job.location.city ? ', ' : ''}${job.location.city || ''}`
                                  : job.location
                              ) : 'Location not specified'}
                            </span>
                          </div>
                          {job.salary && (
                            <div className="flex items-center text-sm text-gray-600">
                              <svg className="h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span className="font-medium">{job.salary}</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Job Description */}
                        {job.description && (
                          <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                            {job.description}
                          </p>
                        )}
                    
                        {/* Skills */}
                        {job.skills && job.skills.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Skills Required</h4>
                            <ul className="flex flex-wrap gap-2" aria-label="Required skills">
                              {job.skills.slice(0, 4).map((skill, index) => (
                                <li key={`${job.id}-skill-${index}`}>
                                  <span 
                                    className="px-3 py-1 text-xs rounded-full font-medium inline-block"
                                    style={{
                                      backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                      color: '#4f46e5'
                                    }}
                                    aria-label={skill}
                                  >
                                    {skill}
                                  </span>
                                </li>
                              ))}
                              {job.skills.length > 4 && (
                                <li>
                                  <span className="px-3 py-1 text-xs text-gray-500">
                                    +{job.skills.length - 4} more
                                  </span>
                                </li>
                              )}
                            </ul>
                          </div>
                        )}
                        
                        {/* Footer */}
                        <div className="pt-4 mt-4 border-t border-gray-100">
                          <div className="flex justify-between items-center">
                            <div className="text-xs text-gray-500">
                              {job.postedAt && (() => {
                                try {
                                  const date = new Date(job.postedAt);
                                  return `Posted ${formatDistanceToNow(date, { addSuffix: true })}`;
                                } catch (e) {
                                  console.warn('Error formatting date:', e);
                                  return 'Posted recently';
                                }
                              })()}
                            </div>
                            
                            {hasApplied ? (
                              <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <FaCheckCircle className="mr-1.5" /> Applied
                              </span>
                            ) : (
                              <div className="flex flex-col items-end">
                                <button
                                  onClick={() => handleApply(job.id, job.title)}
                                  className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-full hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 shadow-sm hover:shadow-md"
                                >
                                  Apply Now
                                </button>
                                {!checkMembershipRequirement().isNewUser && (
                                  <div className="text-xs text-gray-500 mt-1 text-right">
                                    {checkMembershipRequirement().freeApplicationsLeft} free applications left
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-span-full text-center py-10 bg-white rounded-xl shadow">
                <FaExclamationTriangle className="mx-auto text-4xl text-yellow-400 mb-3" />
                <h3 className="text-lg font-medium text-gray-700">No Job Listings Available</h3>
                <p className="text-gray-600 mt-2">
                  {searchQuery || Object.values(filters).some(Boolean)
                    ? 'No jobs match your search criteria. Try adjusting your filters.'
                    : 'There are currently no job listings available. Please check back later.'}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Section 2: My Applications */}
        <section id="my-applications" className="mt-12">
          <h2 className="text-3xl font-semibold text-violet-700 mb-6 flex items-center">
            <FaFileContract className="mr-3" /> My Applications
          </h2>
          
          {applications.length > 0 ? (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Job Title
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Applied On
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {applications.map((application) => (
                    <tr key={application.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {application.jobTitle}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {application.company || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {new Date(application.appliedAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          application.status === 'accepted' 
                            ? 'bg-green-100 text-green-800' 
                            : application.status === 'rejected'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-10 bg-white rounded-lg shadow">
              <FaFileContract className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-lg font-medium text-gray-900">No applications yet</h3>
              <p className="mt-1 text-gray-500">
                You haven't applied to any jobs yet. Find a job that matches your skills and apply!
              </p>
            </div>
          )}
        </section>
      </div>
      
      {/* Modal */}
      {showModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {modalContent.title}
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      {modalContent.message}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 space-y-2">
                {modalContent.showRetryButton && (
                  <button
                    type="button"
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    onClick={() => {
                      if (modalContent.onRetry) modalContent.onRetry();
                      setShowModal(false);
                    }}
                  >
                    Try Again
                  </button>
                )}
                
                {modalContent.showUpgradeButton ? (
                  <button
                    type="button"
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    onClick={() => {
                      if (modalContent.onConfirm) modalContent.onConfirm();
                      setShowModal(false);
                    }}
                  >
                    Upgrade Membership
                  </button>
                ) : (
                  <button
                    type="button"
                    className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium ${
                      modalContent.showRetryButton 
                        ? 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50' 
                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                    onClick={() => setShowModal(false)}
                  >
                    {modalContent.showRetryButton ? 'Cancel' : 'Close'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default ProfessionalDashboard;
